// ============================================================================
// PROJECT TIME TRACKING TAB
// Consolidated time entries from all child jobs and appointments
// Per-person, per-job, per-activity breakdowns with summary cards
// Date: March 2026
// ============================================================================

'use client';

import React, { useState, useEffect, useCallback } from 'react';
import {
  Clock, Users, Briefcase, Car, MapPin, TrendingUp,
  ChevronDown, ChevronRight, RefreshCw, Filter, BarChart3,
} from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { authService } from '@/lib/authService';
import AppointmentReferenceBadge from '@/components/work-items/AppointmentReferenceBadge';
import type { WorkItem } from '../UniversalWorkItems';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ProjectTimeTrackingTabProps {
  workItem: WorkItem;
}

interface TimeEntry {
  id: string;
  work_item_id: string;
  user_id: number;
  user_name: string;
  started_at: string;
  ended_at: string;
  hours: number;
  duration_hours: number;
  duration_minutes: number;
  description: string;
  is_billable: boolean;
  activity_type: string;
  tracked_via: string;
  hourly_rate: number;
  total_cost: number;
  source_item_id: string;
  source_item_number: string;
  source_item_type: string;
  source_item_title: string;
  job_id: string;
  job_number: string;
  job_title: string;
  travel_km: number | null;
}

interface PersonAgg {
  user_id: number;
  user_name: string;
  total_hours: number;
  billable_hours: number;
  travel_hours: number;
  onsite_hours: number;
  travel_distance_km: number;
  entries_count: number;
  jobs_count: number;
}

interface JobAgg {
  job_id: string;
  job_number: string;
  job_title: string;
  job_status: string;
  total_hours: number;
  billable_hours: number;
  travel_hours: number;
  onsite_hours: number;
  travel_distance_km: number;
  entries_count: number;
}

interface ActivityAgg {
  activity_type: string;
  total_hours: number;
  entries_count: number;
}

interface TimeSummary {
  total_hours: number;
  billable_hours: number;
  non_billable_hours: number;
  travel_hours: number;
  onsite_hours: number;
  travel_distance_km: number;
  entry_count: number;
  jobs_count: number;
  people_count: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const VIEWS = ['overview', 'by_person', 'by_job', 'entries'] as const;
type ViewId = typeof VIEWS[number];

const viewMeta: Record<ViewId, { label: string; icon: React.ElementType }> = {
  overview: { label: 'Overview', icon: BarChart3 },
  by_person: { label: 'By Person', icon: Users },
  by_job: { label: 'By Job', icon: Briefcase },
  entries: { label: 'All Entries', icon: Clock },
};

const fmt = (n: number) => n.toFixed(1);
const fmtKm = (n: number) => `${n.toFixed(1)} km`;
const fmtMoney = (n: number) => `R ${n.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const activityLabel = (t: string) => t?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Other';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function ProjectTimeTrackingTab({ workItem }: ProjectTimeTrackingTabProps) {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.mode === 'dark';

  // State
  const [loading, setLoading] = useState(false);
  const [activeView, setActiveView] = useState<ViewId>('overview');
  const [summary, setSummary] = useState<TimeSummary | null>(null);
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [byPerson, setByPerson] = useState<PersonAgg[]>([]);
  const [byJob, setByJob] = useState<JobAgg[]>([]);
  const [byActivity, setByActivity] = useState<ActivityAgg[]>([]);
  const [expandedPersons, setExpandedPersons] = useState<Set<number>>(new Set());
  const [expandedJobs, setExpandedJobs] = useState<Set<string>>(new Set());

  // Helpers
  const getHeaders = useCallback(() => {
    const tokens = authService.getTokens();
    return {
      'Content-Type': 'application/json',
      ...(tokens?.access_token ? { Authorization: `Bearer ${tokens.access_token}` } : {}),
    };
  }, []);

  // Fetch
  const fetchTimeData = useCallback(async () => {
    if (!workItem.id) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/v1/projects/${workItem.id}/time-entries`, { headers: getHeaders() });
      const j = await res.json();
      if (j.success) {
        setSummary(j.data.summary);
        setEntries(j.data.entries || []);
        setByPerson(j.data.by_person || []);
        setByJob(j.data.by_job || []);
        setByActivity(j.data.by_activity || []);
      }
    } catch (err) {
      console.error('Failed to fetch project time entries', err);
    } finally {
      setLoading(false);
    }
  }, [workItem.id, getHeaders]);

  useEffect(() => { fetchTimeData(); }, [fetchTimeData]);

  const togglePerson = (id: number) => setExpandedPersons(prev => {
    const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next;
  });
  const toggleJob = (id: string) => setExpandedJobs(prev => {
    const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next;
  });

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="space-y-5">
      {/* Header with refresh */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className={`text-base font-semibold ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
            Project Time Tracking
          </h3>
          <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            Consolidated from all child jobs &amp; appointments
          </p>
        </div>
        <button
          onClick={fetchTimeData}
          disabled={loading}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition ${
            isDark
              ? 'bg-gray-700 hover:bg-gray-600 text-gray-200 disabled:text-gray-500'
              : 'bg-gray-100 hover:bg-gray-200 text-gray-700 disabled:text-gray-400'
          }`}
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {[
            { label: 'Total Hours', value: `${fmt(summary.total_hours)}h`, icon: Clock, color: 'blue' },
            { label: 'Billable', value: `${fmt(summary.billable_hours)}h`, icon: TrendingUp, color: 'green',
              sub: summary.total_hours > 0 ? `${(summary.billable_hours / summary.total_hours * 100).toFixed(0)}%` : '0%' },
            { label: 'Travel', value: `${fmt(summary.travel_hours)}h`, icon: Car, color: 'amber',
              sub: summary.travel_distance_km > 0 ? fmtKm(summary.travel_distance_km) : undefined },
            { label: 'On-site', value: `${fmt(summary.onsite_hours)}h`, icon: MapPin, color: 'purple' },
            { label: 'People / Jobs', value: `${summary.people_count} / ${summary.jobs_count}`, icon: Users, color: 'indigo',
              sub: `${summary.entry_count} entries` },
          ].map(card => {
            const Icon = card.icon;
            return (
              <div
                key={card.label}
                className={`p-3 rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{card.label}</span>
                  <Icon className={`w-4 h-4 text-${card.color}-500`} />
                </div>
                <div className={`text-lg font-bold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{card.value}</div>
                {card.sub && (
                  <div className={`text-xs mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{card.sub}</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* View Tabs */}
      <div className={`flex border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
        {VIEWS.map(v => {
          const Meta = viewMeta[v];
          const Icon = Meta.icon;
          const isActive = activeView === v;
          return (
            <button
              key={v}
              onClick={() => setActiveView(v)}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition ${
                isActive
                  ? isDark ? 'border-blue-500 text-blue-400' : 'border-blue-600 text-blue-600'
                  : isDark ? 'border-transparent text-gray-400 hover:text-gray-200' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon className="w-4 h-4" />
              {Meta.label}
            </button>
          );
        })}
      </div>

      {/* Loading state */}
      {loading && (
        <div className={`text-center py-8 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
          Loading time entries…
        </div>
      )}

      {/* Empty state */}
      {!loading && summary && summary.entry_count === 0 && (
        <div className={`text-center py-12 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          <Clock className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p className="font-medium">No time entries yet</p>
          <p className="text-sm mt-1">Time will appear here once tracked on child jobs and appointments.</p>
        </div>
      )}

      {/* ======================== OVERVIEW ======================== */}
      {!loading && activeView === 'overview' && byActivity.length > 0 && (
        <div className="space-y-4">
          {/* Activity breakdown */}
          <div className={`rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className={`px-4 py-3 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <h4 className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>Hours by Activity</h4>
            </div>
            <div className="p-4 space-y-3">
              {byActivity.map(act => {
                const pct = summary && summary.total_hours > 0
                  ? (act.total_hours / summary.total_hours * 100)
                  : 0;
                return (
                  <div key={act.activity_type} className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        {activityLabel(act.activity_type)}
                      </span>
                      <span className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                        {fmt(act.total_hours)}h ({pct.toFixed(0)}%)
                      </span>
                    </div>
                    <div className={`h-2 rounded-full ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
                      <div
                        className="h-2 rounded-full bg-blue-500 transition-all"
                        style={{ width: `${Math.min(pct, 100)}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Top contributors quick list */}
          {byPerson.length > 0 && (
            <div className={`rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className={`px-4 py-3 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                <h4 className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>Top Contributors</h4>
              </div>
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {byPerson.slice(0, 5).map(p => (
                  <div key={p.user_id} className="flex items-center justify-between px-4 py-2.5">
                    <div>
                      <span className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>{p.user_name}</span>
                      <span className={`text-xs ml-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{p.jobs_count} job{p.jobs_count !== 1 ? 's' : ''}</span>
                    </div>
                    <div className="text-right">
                      <span className={`text-sm font-semibold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{fmt(p.total_hours)}h</span>
                      {p.travel_distance_km > 0 && (
                        <span className={`text-xs ml-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{fmtKm(p.travel_distance_km)}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ======================== BY PERSON ======================== */}
      {!loading && activeView === 'by_person' && (
        <div className="space-y-2">
          {byPerson.map(p => {
            const isExpanded = expandedPersons.has(p.user_id);
            const personEntries = entries.filter(e => e.user_id === p.user_id);
            const billableRatio = p.total_hours > 0 ? (p.billable_hours / p.total_hours * 100) : 0;
            return (
              <div key={p.user_id} className={`rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                <button
                  onClick={() => togglePerson(p.user_id)}
                  className="w-full flex items-center justify-between px-4 py-3 text-left"
                >
                  <div className="flex items-center gap-3">
                    {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                    <div>
                      <span className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>{p.user_name}</span>
                      <span className={`text-xs ml-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        {p.entries_count} entries • {p.jobs_count} job{p.jobs_count !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <span className={`font-medium ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{fmt(p.total_hours)}h</span>
                    <span className={`${isDark ? 'text-green-400' : 'text-green-600'}`}>{billableRatio.toFixed(0)}% billable</span>
                    {p.travel_distance_km > 0 && (
                      <span className={`${isDark ? 'text-amber-400' : 'text-amber-600'}`}>{fmtKm(p.travel_distance_km)}</span>
                    )}
                  </div>
                </button>
                {isExpanded && personEntries.length > 0 && (
                  <div className={`border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className={isDark ? 'bg-gray-700/50' : 'bg-gray-50'}>
                            <th className={`px-4 py-2 text-left font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Date</th>
                            <th className={`px-4 py-2 text-left font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Job</th>
                            <th className={`px-4 py-2 text-left font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Activity</th>
                            <th className={`px-4 py-2 text-right font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Hours</th>
                            <th className={`px-4 py-2 text-left font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Description</th>
                          </tr>
                        </thead>
                        <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-100'}`}>
                          {personEntries.slice(0, 20).map(e => (
                            <tr key={e.id} className={isDark ? 'hover:bg-gray-700/30' : 'hover:bg-gray-50'}>
                              <td className={`px-4 py-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                {e.started_at ? new Date(e.started_at).toLocaleDateString() : '—'}
                              </td>
                              <td className={`px-4 py-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                <span className="font-mono text-xs">{e.job_number}</span>
                              </td>
                              <td className={`px-4 py-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                {activityLabel(e.activity_type)}
                              </td>
                              <td className={`px-4 py-2 text-right font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                                {fmt(e.hours)}h
                              </td>
                              <td className={`px-4 py-2 max-w-[200px] truncate ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                {e.description || '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {personEntries.length > 20 && (
                        <div className={`px-4 py-2 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                          Showing 20 of {personEntries.length} entries
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {byPerson.length === 0 && !loading && (
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>No team member data available.</p>
          )}
        </div>
      )}

      {/* ======================== BY JOB ======================== */}
      {!loading && activeView === 'by_job' && (
        <div className="space-y-2">
          {byJob.map(j => {
            const isExpanded = expandedJobs.has(j.job_id);
            const jobEntries = entries.filter(e => e.job_id === j.job_id);
            return (
              <div key={j.job_id} className={`rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                <button
                  onClick={() => toggleJob(j.job_id)}
                  className="w-full flex items-center justify-between px-4 py-3 text-left"
                >
                  <div className="flex items-center gap-3">
                    {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                    <div>
                      <span className={`text-sm font-mono ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{j.job_number}</span>
                      <span className={`text-sm font-semibold ml-2 ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>{j.job_title}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-xs px-2 py-0.5 rounded capitalize ${
                      j.job_status === 'completed' ? (isDark ? 'bg-green-900/40 text-green-400' : 'bg-green-100 text-green-700')
                      : j.job_status === 'in_progress' ? (isDark ? 'bg-blue-900/40 text-blue-400' : 'bg-blue-100 text-blue-700')
                      : isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-600'
                    }`}>{j.job_status?.replace(/_/g, ' ')}</span>
                    <span className={`text-sm font-semibold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{fmt(j.total_hours)}h</span>
                  </div>
                </button>
                {isExpanded && (
                  <div className={`border-t px-4 py-3 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                      <div>
                        <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Billable</span>
                        <div className={`text-sm font-medium ${isDark ? 'text-green-400' : 'text-green-600'}`}>{fmt(j.billable_hours)}h</div>
                      </div>
                      <div>
                        <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Travel</span>
                        <div className={`text-sm font-medium ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>{fmt(j.travel_hours)}h</div>
                      </div>
                      <div>
                        <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>On-site</span>
                        <div className={`text-sm font-medium ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>{fmt(j.onsite_hours)}h</div>
                      </div>
                      <div>
                        <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Distance</span>
                        <div className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{j.travel_distance_km > 0 ? fmtKm(j.travel_distance_km) : '—'}</div>
                      </div>
                    </div>
                    {jobEntries.length > 0 && (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className={isDark ? 'bg-gray-700/50' : 'bg-gray-50'}>
                              <th className={`px-3 py-1.5 text-left font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Date</th>
                              <th className={`px-3 py-1.5 text-left font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Person</th>
                              <th className={`px-3 py-1.5 text-left font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Type</th>
                              <th className={`px-3 py-1.5 text-right font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Hours</th>
                            </tr>
                          </thead>
                          <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-100'}`}>
                            {jobEntries.slice(0, 15).map(e => (
                              <tr key={e.id}>
                                <td className={`px-3 py-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                  {e.started_at ? new Date(e.started_at).toLocaleDateString() : '—'}
                                </td>
                                <td className={`px-3 py-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{e.user_name}</td>
                                <td className={`px-3 py-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{activityLabel(e.activity_type)}</td>
                                <td className={`px-3 py-1.5 text-right font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>{fmt(e.hours)}h</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {jobEntries.length > 15 && (
                          <div className={`px-3 py-1.5 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            Showing 15 of {jobEntries.length} entries
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {byJob.length === 0 && !loading && (
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>No job time data available.</p>
          )}
        </div>
      )}

      {/* ======================== ALL ENTRIES ======================== */}
      {!loading && activeView === 'entries' && (
        <div className={`rounded-lg border overflow-hidden ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className={isDark ? 'bg-gray-700/50' : 'bg-gray-50'}>
                  <th className={`px-4 py-2.5 text-left font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Date</th>
                  <th className={`px-4 py-2.5 text-left font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Person</th>
                  <th className={`px-4 py-2.5 text-left font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Job</th>
                  <th className={`px-4 py-2.5 text-left font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Source</th>
                  <th className={`px-4 py-2.5 text-left font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Activity</th>
                  <th className={`px-4 py-2.5 text-right font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Hours</th>
                  <th className={`px-4 py-2.5 text-center font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Billable</th>
                  <th className={`px-4 py-2.5 text-left font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Description</th>
                </tr>
              </thead>
              <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-100'}`}>
                {entries.slice(0, 100).map(e => (
                  <tr key={e.id} className={isDark ? 'hover:bg-gray-700/30' : 'hover:bg-gray-50'}>
                    <td className={`px-4 py-2 whitespace-nowrap ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      {e.started_at ? new Date(e.started_at).toLocaleDateString() : '—'}
                    </td>
                    <td className={`px-4 py-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{e.user_name}</td>
                    <td className={`px-4 py-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      <span className="font-mono text-xs">{e.job_number}</span>
                    </td>
                    <td className={`px-4 py-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      {e.source_item_type === 'appointment' ? (
                        <AppointmentReferenceBadge
                          appointmentId={e.source_item_id}
                          appointmentNumber={e.source_item_number}
                          className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full ${isDark ? 'bg-indigo-900/40 text-indigo-300 hover:bg-indigo-900/60' : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'} transition-colors`}
                          iconClassName="w-3 h-3"
                          title={`Open appointment ${e.source_item_number}`}
                        >
                          <>
                            <span>📋 {e.source_item_number}</span>
                          </>
                        </AppointmentReferenceBadge>
                      ) : (
                        <span className="text-xs">{e.source_item_number} ({e.source_item_type})</span>
                      )}
                    </td>
                    <td className={`px-4 py-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{activityLabel(e.activity_type)}</td>
                    <td className={`px-4 py-2 text-right font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>{fmt(e.hours)}h</td>
                    <td className="px-4 py-2 text-center">
                      {e.is_billable ? (
                        <span className={`text-xs px-1.5 py-0.5 rounded ${isDark ? 'bg-green-900/40 text-green-400' : 'bg-green-100 text-green-700'}`}>Yes</span>
                      ) : (
                        <span className={`text-xs px-1.5 py-0.5 rounded ${isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>No</span>
                      )}
                    </td>
                    <td className={`px-4 py-2 max-w-[180px] truncate ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      {e.description || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {entries.length === 0 && (
              <div className={`text-center py-8 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>No entries found</div>
            )}
            {entries.length > 100 && (
              <div className={`px-4 py-2 text-xs border-t ${isDark ? 'border-gray-700 text-gray-500' : 'border-gray-200 text-gray-400'}`}>
                Showing 100 of {entries.length} entries
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
